import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronRight, Edit } from "lucide-react";
import { Rule } from "./Rule";
import type { TemplateFieldRuleSet } from "@/integrations/supabase/templateEditor";
import type { TemplateFieldWithDatapoints } from "./Rule";
type RuleSet = TemplateFieldRuleSet;

interface RuleSetProps {
  availableTemplateFields: TemplateFieldWithDatapoints[];
  ruleSet: RuleSet;
  onUpdateRuleSetMetadata: (name: string, description?: string) => void;
  onCreateRule: (
    ruleSetId: string,
    rule: {
      fieldId: string;
      datapointId: string;
      operator: "AND" | "NOT";
    },
  ) => void;
  onDeleteRule: (ruleSetId: string, ruleId: string) => void;
  onUpdateRule: (
    ruleId: string,
    updateArgs: {
      fieldId: string;
      datapointId: string;
      operator: "AND" | "NOT";
    },
  ) => void;
  onDeleteRuleSet: (ruleSetId: string) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

export const RuleSet = ({
  availableTemplateFields,
  ruleSet,
  onUpdateRuleSetMetadata,
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
  onDeleteRuleSet,
  isExpanded,
  onToggleExpanded,
}: RuleSetProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(ruleSet.name);

  const [temporaryRules, setTemporaryRules] = useState<TemplateFieldRuleSet["rules"]>([]);
  const createTemporaryRule = () => {
    setTemporaryRules((prev) => {
      const newRule = {
        id: `temp_${Date.now()}`,
        field_id: "",
        datapoint_id: "",
        operator: "AND" as const,
        order_index: ruleSet.rules.length + prev.length,
        datapoint: {
          id: "",
          name: "",
        },
        field: {
          id: "",
          name: "",
        },
        isTemporary: true,
      };
      return [...prev, newRule];
    });
  };

  const handleDeleteTempRule = (tempRuleId: string) => {
    setTemporaryRules((prev) => prev.filter((r) => r.id !== tempRuleId));
  };

  const handleDeleteRuleSet = () => {
    if (confirm("Are you sure you want to delete this rule set?")) {
      onDeleteRuleSet(ruleSet.id);
    }
  };

  const handleUpdateTempRule = useCallback(
    (
      tempRuleId: string,
      updateArgs: {
        fieldId: string;
        datapointId: string;
        operator: "AND" | "NOT";
      },
    ) => {
      if (updateArgs.fieldId && updateArgs.datapointId && updateArgs.operator) {
        onCreateRule(ruleSet.id, {
          fieldId: updateArgs.fieldId,
          datapointId: updateArgs.datapointId,
          operator: updateArgs.operator,
        });
        setTemporaryRules((prev) => prev.filter((r) => r.id !== tempRuleId));
      } else {
        setTemporaryRules((prev) => prev.map((r) => (r.id === tempRuleId ? { ...r, ...updateArgs } : r)));
      }
    },
    [onCreateRule, ruleSet.id],
  );

  const handleDeleteRule = (ruleId: string) => {
    onDeleteRule(ruleSet.id, ruleId);
  };

  const handleUpdateRule = (
    ruleId: string,
    updateArgs: {
      fieldId: string;
      datapointId: string;
      operator: "AND" | "NOT";
    },
  ) => {
    onUpdateRule(ruleId, updateArgs);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-muted/30 p-3 flex items-center justify-between">
        {isEditing ? (
          <div className="flex items-center flex-grow gap-2">
            <Input value={ruleSet.name} onChange={(e) => setName(e.target.value)} className="h-8 max-w-xs" autoFocus />
            <Button size="sm" onClick={() => onUpdateRuleSetMetadata(name)}>
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setName(ruleSet.name);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onToggleExpanded}>
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            {ruleSet.name}
            <span className="text-xs text-muted-foreground">
              ({ruleSet.rules.length} {ruleSet.rules.length === 1 ? "rule" : "rules"})
            </span>
          </div>
        )}

        <div className="flex items-center gap-1">
          {!isEditing && (
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={handleDeleteRuleSet}
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Delete</span>
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-4">
          {ruleSet.rules.map((rule) => (
            <Rule
              key={rule.id}
              rule={rule}
              availableTemplateFields={availableTemplateFields}
              onUpdate={(updateArgs) => handleUpdateRule(rule.id, updateArgs)}
              onDelete={() => handleDeleteRule(rule.id)}
            />
          ))}
          {temporaryRules.map((rule) => (
            <Rule
              key={rule.id}
              rule={rule}
              availableTemplateFields={availableTemplateFields}
              onUpdate={(updateArgs) => handleUpdateTempRule(rule.id, updateArgs)}
              onDelete={() => handleDeleteTempRule(rule.id)}
            />
          ))}
          <Button variant="outline" size="sm" onClick={createTemporaryRule} className="w-full">
            Add Rule
          </Button>
        </div>
      )}
    </div>
  );
};
